/**
 * Maps amenity / utility names to DOMAIN ICON REGISTRY ids
 * (components/base/icons/domain/registry.tsx) — rendered via <DomainIcon>.
 *
 * Amenities are stored in the DB as English labels (see AMENITIES_LIST in
 * propertyFormOptions), but older rows may hold Arabic strings — so every
 * entry lists all known aliases (id, English label, Arabic label, common
 * variants). One id per concept, one glyph per id, sitewide.
 */
import type { DomainIconId } from '../components/base/icons/domain/registry';

interface IdEntry {
  id: DomainIconId;
  names: string[];
}

const AMENITY_ENTRIES: IdEntry[] = [
  { id: 'parking', names: ['parking', 'موقف سيارات', 'covered parking', 'موقف مغطى'] },
  { id: 'elevator', names: ['elevator', 'مصعد'] },
  { id: 'pool', names: ['pool', 'swimming pool', 'مسبح', 'private pool', 'مسبح خاص', 'shared pool', 'مسبح مشترك'] },
  { id: 'garden', names: ['garden', 'حديقة', 'private garden', 'حديقة خاصة'] },
  { id: 'balcony', names: ['balcony', 'شرفة'] },
  { id: 'roof', names: ['roof', 'private roof', 'سطح خاص'] },
  { id: 'driver_room', names: ['driver_room', 'driver room', 'غرفة سائق'] },
  { id: 'maid_room', names: ['maid_room', 'maid room', 'غرفة خادمة'] },
  { id: 'storage', names: ['storage', 'تخزين', 'مخزن'] },
  { id: 'security', names: ['security', 'حراسة', 'security system', 'نظام أمني'] },
  { id: 'intercom', names: ['intercom', 'اتصال داخلي'] },
  { id: 'central_ac', names: ['central_ac', 'central ac', 'تكييف مركزي', 'ac', 'air conditioning', 'central air conditioning', 'تكييف'] },
  { id: 'gym', names: ['gym', 'صالة رياضية'] },
  { id: 'playground', names: ['playground', 'ملعب أطفال'] },
  { id: 'smart_home', names: ['smart_home', 'smart home', 'منزل ذكي'] },
  { id: 'basement', names: ['basement', 'قبو'] },
  { id: 'concierge', names: ['concierge', 'خدمة استقبال', 'reception', 'استقبال'] },
  { id: 'pet_friendly', names: ['pet_friendly', 'pet friendly', 'pets allowed', 'مسموح بالحيوانات'] },
  { id: 'guest_house', names: ['guest_house', 'guest house', 'بيت ضيافة'] },
  { id: 'courtyard', names: ['courtyard', 'فناء'] },
  { id: 'furnished', names: ['furnished', 'مفروش', 'semi-furnished', 'مفروش جزئياً', 'unfurnished', 'غير مفروش'] },
  { id: 'kitchen', names: ['kitchen', 'مطبخ', 'equipped kitchen', 'مطبخ مجهز'] },
  { id: 'laundry', names: ['laundry', 'laundry room', 'غسيل', 'غرفة غسيل'] },
  { id: 'walk_in_closet', names: ['walk-in closet', 'غرفة ملابس'] },
  { id: 'private_entrance', names: ['private entrance', 'مدخل خاص'] },
  { id: 'study_room', names: ['study room', 'غرفة دراسة'] },
  { id: 'fireplace', names: ['fireplace', 'مدفأة', 'bbq area', 'منطقة شواء'] },
  { id: 'sauna', names: ['sauna', 'ساونا', 'central heating', 'تدفئة مركزية'] },
  { id: 'jacuzzi', names: ['jacuzzi', 'جاكوزي'] },
  { id: 'lobby', names: ['lobby', 'ردهة'] },
  { id: 'cctv', names: ['cctv', 'surveillance', 'كاميرات مراقبة'] },
  { id: 'guard', names: ['guard', 'حارس أمن'] },
  { id: 'fire_alarm', names: ['fire alarm', 'إنذار حريق'] },
  { id: 'generator', names: ['backup generator', 'مولد احتياطي'] },
  { id: 'double_glazing', names: ['double glazing', 'زجاج مزدوج'] },
  { id: 'soundproofing', names: ['soundproofing', 'عزل صوتي'] },
  { id: 'wheelchair', names: ['wheelchair access', 'وصول لذوي الاحتياجات'] },
];

const UTILITY_ENTRIES: IdEntry[] = [
  { id: 'water', names: ['water', 'ماء', 'الماء'] },
  { id: 'electricity', names: ['electricity', 'كهرباء', 'الكهرباء'] },
  { id: 'internet', names: ['internet', 'إنترنت', 'high-speed internet', 'إنترنت عالي السرعة'] },
  { id: 'gas', names: ['gas', 'غاز'] },
  { id: 'sewage', names: ['sewage', 'صرف صحي'] },
  { id: 'trash_collection', names: ['trash collection', 'جمع نفايات'] },
  { id: 'district_cooling', names: ['district cooling', 'تبريد مركزي'] },
  { id: 'phone', names: ['phone', 'هاتف'] },
  { id: 'fiber', names: ['fiber', 'ألياف ضوئية', 'fiber optic'] },
  { id: 'flood_drainage', names: ['flood_drainage', 'flood drainage', 'تصريف الفيضانات'] },
];

function buildLookup(entries: IdEntry[]): Record<string, DomainIconId> {
  const lookup: Record<string, DomainIconId> = {};
  for (const entry of entries) {
    for (const name of entry.names) {
      lookup[name.toLowerCase()] = entry.id;
    }
  }
  return lookup;
}

const amenityLookup = buildLookup(AMENITY_ENTRIES);
const utilityLookup = buildLookup(UTILITY_ENTRIES);

/** Registry id for an amenity name/alias, with a check-circle fallback. */
export function getAmenityIconId(amenity: string): DomainIconId {
  return amenityLookup[amenity.trim().toLowerCase()] ?? 'amenity';
}

/** Registry id for a utility name/alias, with a plug fallback. */
export function getUtilityIconId(utility: string): DomainIconId {
  return utilityLookup[utility.trim().toLowerCase()] ?? 'utility';
}
